import Signal from './Signal';

type CleanupFunction = () => void;

export type UnpackObservable<T> = (
    T extends IObservableValuePublic<infer I> ? I :
    T extends IObservableValuePrivate<infer I> ? I :
    T extends ObservableValue<infer I> ? I :
    never
);

export interface IObservableValuePublic<T> {
    readonly onChange: Signal<[T, T]>;
    getValue: () => T;
    subscribe: (listener: (value: T) => void) => CleanupFunction;
}

export interface IObservableValuePrivate<T> {
    setValue: (newValue: T) => boolean;
}

export class ObservableValue<T> {
    public readonly read: IObservableValuePublic<T>;
    public readonly control: IObservableValuePrivate<T>;
    private value: T;

    constructor(
        value: T,
    ) {
        this.value = value;

        this.read = {
            getValue: () => this.value,
            subscribe: listener => {
                const cleanup = this.read.onChange.addListener(listener);
                listener(this.value);
                return cleanup;
            },
            onChange: new Signal(),
        };
        this.control = {
            setValue: this.setValue,
        };
    }

    private setValue = (newValue: T): boolean => {
        const oldValue: T = this.value;
        this.value = newValue;
        const changed: boolean = this.value !== oldValue;
        if (changed) {
            this.read.onChange.dispatch(newValue, oldValue);
        }
        return changed;
    };
}

/**
 * @param shouldResolve Can throw an error to reject the promise
 */
export function promisify<T>(
    observableRead: Pick<IObservableValuePublic<T>, 'subscribe'>,
    shouldResolve: (value: T) => boolean
): Promise<T> {
    return new Promise(
        (
            resolve: (result: T) => void,
            reject: (error: Error) => void
        ) => {
            let flagForRemoval = false;
            let removeOnChangeListener: CleanupFunction | null = observableRead.subscribe(
                (newValue: T) => {
                    let succeed = false;
                    try {
                        succeed = shouldResolve(newValue);
                    } catch (error) {
                        tryRemoveOnChangeListener();
                        reject(error);
                        return;
                    }
                    if (succeed) {
                        tryRemoveOnChangeListener();
                        resolve(newValue);
                    }
                }
            );
            // In case `removeOnChangeListener` was attempted to be called before it got assigned
            if (flagForRemoval) {
                tryRemoveOnChangeListener();
            }
            return;
            function tryRemoveOnChangeListener() {
                flagForRemoval = true;
                // Might be not set yet, or unset already
                if (removeOnChangeListener) {
                    const copy = removeOnChangeListener;
                    removeOnChangeListener = null;
                    copy();
                }
            }
        }
    );
}

/**
 * Turns an observable value with an array subtype to a promise
 * that resolves with element at given index once it becomes available (based on size of the array)
 */
export async function promisifyIndex<T>(
    observableRead: Pick<IObservableValuePublic<Array<T>>, 'subscribe'>,
    index: number
): Promise<T> {
    const arrayWithRequiredIndex = await promisify(
        observableRead,
        array => {
            const lastIndex = array.length - 1;
            return lastIndex >= index;
        }
    );
    return arrayWithRequiredIndex[index];
}
